import { useCallback } from "react";
import { getOptionByIdWithCORP } from "../../api/TabwareOptions";
import { getPreSignedUploadUrl } from "../../api/Documents";
import { ApiResponse } from "../../models/api/ApiResponse";
import { ErrorCodes, ErrorInfo } from "../../util/ErrorHelper";
import { createViaCopy } from "../../api/WorkOrders";
import useGetEquipmentAreaDepartment from "../equipment/useGetEquipmentAreaDepartment";

export default function useCreateWo() {
  const { getEquipmentAreaDepartment } = useGetEquipmentAreaDepartment();

  const uploadAllDocuments = useCallback(async (document: any[] = []) => {
    if (!document?.length) return;
    let documentStorageType = await getOptionByIdWithCORP(
      "DocumentsStorageType"
    );
    let documentStorageValue = documentStorageType.data.PlantValue
      ? documentStorageType.data.PlantValue
      : documentStorageType.data.DefaultValue;
    if (documentStorageValue === "Aptean Drive") {
      for (const doc of document ?? []) {
        let presignedUploadResponse: ApiResponse = await getPreSignedUploadUrl(
          doc.FileName
        );
        if (
          presignedUploadResponse.status !== 200 ||
          presignedUploadResponse.isError
        ) {
          throw new ErrorInfo(
            ErrorCodes.Document_Upload_Failed,
            presignedUploadResponse.data
          );
        }
        const fileContent = new Blob(
          [Uint8Array.from(atob(doc.DocContentBase64), (c) => c.charCodeAt(0))],
          { type: doc.File.type }
        );
        const uploadResponse = await fetch(
          presignedUploadResponse.data.PresignedUploadUrl,
          {
            method: "PUT",
            body: fileContent,
          }
        );

        if (!uploadResponse.ok) {
          throw new ErrorInfo(
            ErrorCodes.Document_Upload_Failed,
            "Failed to upload file to Wasabi."
          );
        }
        doc.DocContentBase64 = "";
        doc.File = null;
        doc.FilePath = presignedUploadResponse.data.FilePath;
      }
    }
  }, []);

  const createWo = useCallback(
    async (data: { confirmation: any; requestObject: any }) => {
      await uploadAllDocuments(data.requestObject?.Documents);

      // Bug 917416: for a copy-from-SR (SrBase present) the template already
      // resolved Area/Department via EAM's WO-101-DEF-EQP-THEN-SR precedence
      // (equipment first, SR fallback when blank). Overwriting them with the
      // raw equipment values here would wipe the SR fallback, so only apply the
      // equipment default for the normal (non-SR) create path.
      let requestObject = data.requestObject;
      if (!data.requestObject.SrBase) {
        const { area, department } = await getEquipmentAreaDepartment(
          data.requestObject.Equipment
        );
        requestObject = {
          ...requestObject,
          Area: area,
          Department: department,
        };
      }

      const payload = {
        Confirmation: data.confirmation,
        RequestObject: requestObject,
      };
      const response = await createViaCopy(payload);
      return response;
    },
    [getEquipmentAreaDepartment, uploadAllDocuments]
  );

  return { createWo };
}
